import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { 
  AlertCircle, 
  ArrowLeft, 
  CalendarClock, 
  ExternalLink
} from "lucide-react";
import { OperationsDashboardService } from "@/services/operationsDashboardService";
import { EmployerDetail as EmployerDetailType, OperationalIssueDetail } from "@/types/operations";
import { formatDateToBritish } from "@/lib/dateUtils";
import { Button } from "@/components/ui/button";
import DashboardContainer from "@/components/layout/DashboardContainer";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

const EmployerDetailView: React.FC = () => {
  const { employerId } = useParams<{ employerId: string }>();
  const navigate = useNavigate();
  const [employerDetail, setEmployerDetail] = useState<EmployerDetailType | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchEmployerDetail = async () => {
      if (!employerId) return;
      
      try {
        setIsLoading(true);
        setError(null);
        const data = await OperationsDashboardService.getEmployerDetail(Number(employerId));
        setEmployerDetail(data);
      } catch (err) {
        setError("Failed to load employer details. Please try again later.");
        console.error(err);
      } finally {
        setIsLoading(false);
      }
    };

    fetchEmployerDetail();
  }, [employerId]);

  // Get severity badge with appropriate styling
  const getSeverityBadge = (severity: string) => {
    if (severity === "CRITICAL") {
      return <Badge variant="destructive" className="flex items-center">
        <AlertCircle className="w-3 h-3 mr-1" />
        {severity}
      </Badge>;
    }
    return <Badge variant="outline" className="flex items-center border-yellow-500 text-yellow-700">
      <CalendarClock className="w-3 h-3 mr-1" />
      {severity}
    </Badge>;
  };

  // Handle issue click to navigate to issue detail
  const handleIssueClick = (issueId: number) => {
    navigate(`/operations/issues/${issueId}`);
  };

  // Navigate back to employer list
  const handleBackClick = () => {
    navigate("/operations/dashboard");
  };

  if (isLoading) {
    return (
      <DashboardContainer>
        <div className="flex justify-center items-center py-8">
          <p className="text-gray-500">Loading employer details...</p>
        </div>
      </DashboardContainer>
    );
  }

  if (error || !employerDetail) {
    return (
      <DashboardContainer>
        <div className="flex justify-center items-center py-8">
          <div className="bg-red-50 text-red-700 px-4 py-3 rounded-lg">
            <p className="flex items-center">
              <AlertCircle className="w-5 h-5 mr-2" />
              {error || "Employer not found"}
            </p>
          </div>
        </div>
      </DashboardContainer>
    );
  }

  return (
    <DashboardContainer
      title={`Employer: ${employerDetail.employer_email}`}
      headerContent={
        <Button 
          variant="outline" 
          size="sm" 
          className="flex items-center" 
          onClick={handleBackClick}
        >
          <ArrowLeft className="w-4 h-4 mr-1" />
          Back to List
        </Button>
      }
    >
      <div className="space-y-6">
        {/* Employer Info */}
        <Card>
          <CardHeader>
            <CardTitle>Employer Information</CardTitle>
            <CardDescription>
              Basic information about the employer and employee
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <h3 className="text-sm font-medium text-gray-500">Employer Email</h3>
                <p className="mt-1">{employerDetail.employer_email}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Tab Navigation */}
        <Tabs defaultValue="issues" className="w-full">
          <TabsList className="mb-4">
            <TabsTrigger value="issues">Active Issues</TabsTrigger>
            <TabsTrigger value="payruns">Recent Payruns</TabsTrigger>
          </TabsList>

          {/* Active Issues Tab */}
          <TabsContent value="issues">
            <Card>
              <CardHeader>
                <CardTitle>Active Issues</CardTitle>
                <CardDescription>
                  Review and resolve active issues for this employer
                </CardDescription>
              </CardHeader>
              <CardContent>
                {employerDetail.active_issues.length === 0 ? (
                  <div className="text-center py-6 text-gray-500">
                    No active issues found for this employer
                  </div>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Issue Type</TableHead>
                        <TableHead>Severity</TableHead>
                        <TableHead className="hidden md:table-cell">Description</TableHead>
                        <TableHead className="hidden md:table-cell">Created</TableHead>
                        <TableHead className="w-[100px]"></TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {employerDetail.active_issues.map((issue: OperationalIssueDetail) => (
                        <TableRow 
                          key={issue.id}
                          className="hover:bg-gray-50 cursor-pointer"
                          onClick={() => handleIssueClick(issue.id)}
                        >
                          <TableCell className="font-medium">{issue.issue_type}</TableCell>
                          <TableCell>{getSeverityBadge(issue.severity)}</TableCell>
                          <TableCell className="hidden md:table-cell max-w-xs truncate">
                            {issue.description}
                          </TableCell>
                          <TableCell className="hidden md:table-cell">
                            {formatDateToBritish(issue.created_at)}
                          </TableCell>
                          <TableCell>
                            <Button 
                              variant="ghost" 
                              size="sm" 
                              className="flex items-center" 
                              onClick={(e) => {
                                e.stopPropagation();
                                handleIssueClick(issue.id);
                              }}
                            >
                              <ExternalLink className="w-4 h-4 mr-1" />
                              View
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Recent Payruns Tab */}
          <TabsContent value="payruns">
            <Card>
              <CardHeader>
                <CardTitle>Recent Payruns</CardTitle>
                <CardDescription>
                  View the most recent payruns for this employer
                </CardDescription>
              </CardHeader>
              <CardContent>
                {employerDetail.recent_payruns.length === 0 ? (
                  <div className="text-center py-6 text-gray-500">
                    No recent payruns found for this employer
                  </div>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Payrun ID</TableHead>
                        <TableHead>Pay Date</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead className="w-[100px]"></TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {employerDetail.recent_payruns.map((payrun) => (
                        <TableRow key={payrun.id}>
                          <TableCell className="font-medium">{payrun.id}</TableCell>
                          <TableCell>
                            {payrun.payDate ? formatDateToBritish(payrun.payDate) : "N/A"}
                          </TableCell>
                          <TableCell>
                            <Badge className={
                              payrun.status === "approved" ? "bg-green-100 text-green-800" : 
                              payrun.status === "pending" ? "bg-yellow-100 text-yellow-800" : 
                              "bg-gray-100 text-gray-800"
                            }>
                              {payrun.status || "N/A"}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            <Button variant="ghost" size="sm">
                              View
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </DashboardContainer>
  );
};

export default EmployerDetailView; 